<script setup>
import { computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import 'devextreme-vue/text-area'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import * as dataGrid from '@/composables/data-grid'

import { useRegistryStore } from '@/stores/registry.store'

import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'

const props = defineProps({
  product: {
    type: Object,
    required: true
  },
  entityLock: {
    type: Object,
    required: true
  },
})

const productId = props.product.id
const productCode = props.product.code
const gridId = 'weight-grid'

const i18n = getI18n()
const registryStore = useRegistryStore()

const { gridRef } = dataGrid.useGridRef()

const gridDataSource = dataGrid.useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.productWeightsUrl)
gridDataSource.filter(['product_ref', '=', productId])

const rowButtons = props.entityLock.getEntityLockButtons()

const columns = [
  {
    type: 'buttons',
    buttons: [{template: 'editButtonsTemplate'}],
    width: '6rem',
    fixed: false
  },
  { dataField: 'gross_weight', caption: i18n.t('common.grossWeight'), dataType: 'number', width: '16rem' },
  { dataField: 'net_weight', caption: i18n.t('common.netWeight'), dataType: 'number', width: '16rem' },
  { dataField: 'height', caption: i18n.t('common.height'), dataType: 'number', width: '16rem' },
  { dataField: 'length', caption: i18n.t('common.length'), dataType: 'number', width: '16rem' },
  { dataField: 'depth', caption: i18n.t('common.depth'), dataType: 'number', width: '16rem' },
  { dataField: 'volume', caption: i18n.t('common.volume'), dataType: 'number', width: '16rem' },
]

const { defaultActionTitle, defaultItemTitle } = dataGrid.useDefaultDialogTitle()

const dialogTitle = (i) => {
  let title = `${defaultActionTitle(i)} ${i18n.t('common.weightAndMeasure')} ${productCode}`
  const defaultItemTitleValue = defaultItemTitle(props.templateData?.data)
  if (defaultItemTitleValue) {
    title += ` ${defaultItemTitleValue}`
  }
  return title
}

const editing = computed(() =>
  dataGrid.useDefaultEditing(dialogTitle, {
    items: [
      {
        itemType: 'group',
        caption: i18n.t('common.mainInfo'),
        colCount: 3,
        items: [
          { dataField: 'gross_weight'},
          { dataField: 'net_weight'},
          { dataField: 'height'},
          { dataField: 'length'},
          { dataField: 'depth'},
          { dataField: 'volume'},
        ]
      },
    ]
  }, {
    allowAdding: false,
    allowUpdating: (e) => !e.row.data.is_portal && !props.entityLock?.isLocked(false, productId),
    allowDeleting: (e) => !e.row.data.is_portal && !props.entityLock?.isLocked(false, productId),
    onInitializedCustom: (e) => props.entityLock.readOnlyToolbarItems(e),
  })
)

const onInitNewRow = (e) => {
  e.data.product_ref = productId
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: gridDataSource,
  columns: columns,
  editing: editing.value,
  paging: { enabled: false },
  pager: { visible: false },
  columnChooser: { enabled: false },
  onInitNewRow: onInitNewRow,
  onToolbarPreparing: (e) => dataGrid.useDefaultToolbar(e, {}),
  onContentReady: (e) => {
    e.component.option('editing.allowAdding', !(e.component.totalCount() > 0))
  },
  height: '100%',
})

</script>
<template>
  <dx-data-grid
    ref="gridRef"
    v-bind="gridOptions"
  >
    <template
      #editButtonsTemplate="{ data: cellInfo }"
    >
      <edit-lock-buttons-template
        :entity-name="'Product'"
        :master-entity-id="productId"
        :cell-info="cellInfo"
        :grid="gridRef"
        :entity-lock="props.entityLock"
        :buttons="rowButtons"
      />
    </template>
    <template #customToolbarAddButtonTemplate>
      <custom-toolbar-add-button-template
        :master-entity-id="productId"
        :grid="gridRef"
        :entity-lock="props.entityLock"
      />
    </template>
  </dx-data-grid>
</template>
<style>
</style>
